import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Chip, ThemeProvider } from "@mui/material";
import Footer from "components/HomePage/Footer";
import Header from "components/HomePage/Header";
import dayjs, { Dayjs } from "dayjs";
import {
  ArrowRight,
  CheckCircle,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  User,
  UserCheck,
} from "lucide-react";
import { Territory } from "models/territory";
import { Education, Experience } from "models/user";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { v4 as uuid } from "uuid";
import { AxiosHttpClient } from "../../services/AxiosHttpService";
import {
  removeSignUpActionFailure,
  signUpAction,
} from "../../store/Signup/actions";
import { isUserSignupFailed, isUserSignupSucceed } from "../../store/selectors";
import { Dispatch, RootState } from "../../store/types";
import { CustomTheme } from "../../styles/Theme";
import AutocompleteInfiniteScroll from "../Reusable/AutocompleteInfiniteScroll";
import { AutocompleteLabeled } from "../Reusable/AutocompleteLabeled";
import SelectLabeled from "../Reusable/SelectLabeled";
import TextFieldLabeled from "../Reusable/TextFieldLabeled";

// Liste des langues disponibles
const languages = [
  "English",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Portuguese",
  "Chinese",
  "Japanese",
  "Arabic",
  "Russian",
];

interface FormErrors {
  title?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  entityName?: string;
  professionalPosition?: string;
  location?: string;
  password?: string;
  confirmPassword?: string;
  professionalTitle?: string;
  specialties?: string;
  description?: string;
  acceptTerms?: string;
  jurisdictions?: string;
}

interface SignUpPageProps {
  isUserSignupSucceed: boolean;
  isUserSignupFailed: boolean;
  signUp: typeof signUpAction;
  removeSignUpFailure: typeof removeSignUpActionFailure;
}

const _RegisterPage: React.FC<SignUpPageProps> = ({
  isUserSignupSucceed,
  isUserSignupFailed,
  signUp,
  removeSignUpFailure,
}) => {
  // State hooks for form fields
  const [inputTitle, setInputTitle] = useState("");
  const [inputFirstName, setInputFirstName] = useState("");
  const [inputLastName, setInputLastName] = useState("");
  const [inputEmail, setInputEmail] = useState("");
  const [inputPhoneNumber, setInputPhoneNumber] = useState("");
  const [inputEntityName, setInputEntityName] = useState("");
  const [inputProfessionalPosition, setInputProfessionalPosition] =
    useState("");
  const [inputLocation, setInputLocation] = useState<Territory | null>(null);
  const [inputLocationText, setInputLocationText] = useState("");
  const [inputPassword, setInputPassword] = useState("");
  const [inputConfirmPassword, setInputConfirmPassword] = useState("");
  const [inputLanguage, setInputLanguage] = useState("fr");
  const [inputIsExpert, setInputIsExpert] = useState(false);
  const [inputExpertiseDomain, setInputExpertiseDomain] = useState("");
  const [inputSpecialties, setInputSpecialties] = useState<string[]>([]);
  const [inputDescription, setInputDescription] = useState("");
  const [inputLanguages, setInputLanguages] = useState<string[]>([]);
  const [inputBarAssociation, setInputBarAssociation] = useState("");
  const [inputEducations, setInputEducations] = useState<Education[]>([]);
  const [inputExperiences, setInputExperiences] = useState<Experience[]>([]);
  const [inputAcceptTerms, setInputAcceptTerms] = useState(false);
  const [inputJurisdictions, setInputJurisdictions] = useState<Territory[]>([]);
  const [inputJurisdictionsText, setInputJurisdictionsText] = useState("");

  // Territory management states
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [jurisdictionsTerritories, setJurisdictionsTerritories] = useState<
    Territory[]
  >([]);
  const [isLoadingTerritories, setIsLoadingTerritories] = useState(false);
  const [isLoadingJurisdictions, setIsLoadingJurisdictions] = useState(false);
  const [page, setPage] = useState(1);
  const [pageJurisdictions, setPageJurisdictions] = useState(1);
  const [totalNumberOfTerritories, setTotalNumberOfTerritories] = useState(0);
  const [
    totalNumberOfJurisdictionsTerritories,
    setTotalNumberOfJurisdictionsTerritories,
  ] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [hasMoreJurisdictions, setHasMoreJurisdictions] = useState(true);

  // Other state variables
  const [success, setSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Controllers for API calls
  const previousController = useRef<AbortController>(null!);
  const previousControllerJurisdictions = useRef<AbortController>(null!);

  const { t } = useTranslation();

  // API functions for territories
  const getTerritoriesPage = async (pageNumber?: number) => {
    if (previousController.current) {
      previousController.current.abort();
    }
    setIsLoadingTerritories(true);
    const controller = new AbortController();
    const signal = controller.signal;
    previousController.current = controller;
    try {
      const res = await AxiosHttpClient.getForNonAuth<any>(
        "api/v1/docuveritas/territories/page",
        {
          criteriaList:
            "name=" +
            inputLocationText +
            ",featureClass=P,population=5000=greaterThanEqual",
          pageNumber: (pageNumber ? pageNumber : page) - 1,
          pageSize: 20,
          sort: "name",
          order: "asc",
        },
        signal
      );
      setTotalNumberOfTerritories(Number(res.totalElements));
      (pageNumber ? pageNumber : page) === 1
        ? setTerritories([...res.content])
        : setTerritories([...territories, ...res.content]);
      setPage((pageNumber ? pageNumber : page) === 1 ? 2 : page + 1);
    } catch (error) {
      // Handle error silently
    } finally {
      setIsLoadingTerritories(false);
    }
  };

  const getJurisdictionTerritoriesPage = (pageNumber?: number) => {
    if (previousControllerJurisdictions.current) {
      previousControllerJurisdictions.current.abort();
    }
    setIsLoadingJurisdictions(true);
    const controller = new AbortController();
    const signal = controller.signal;
    previousControllerJurisdictions.current = controller;
    AxiosHttpClient.getForNonAuth<any>(
      "api/v1/docuveritas/territories/page",
      {
        criteriaList: "id=2542007",
        pageNumber: (pageNumber ? pageNumber : pageJurisdictions) - 1,
        pageSize: 20,
        sort: "name",
        order: "asc",
      },
      signal
    )
      .then((res) => {
        setTotalNumberOfJurisdictionsTerritories(Number(res.totalElements));
        (pageNumber ? pageNumber : pageJurisdictions) === 1
          ? setJurisdictionsTerritories([...res.content])
          : setJurisdictionsTerritories([
              ...jurisdictionsTerritories,
              ...res.content,
            ]);
      })
      .catch(() => {})
      .finally(() => setIsLoadingJurisdictions(false));
  };

  const fetchMoreData = async (): Promise<Territory[]> => {
    if (page > totalNumberOfTerritories / 20) {
      await getTerritoriesPage(page);
      setHasMore(false);
      return territories;
    }
    await getTerritoriesPage(page);
    return territories;
  };

  const fetchMoreJurisdictionsData = async (): Promise<Territory[]> => {
    if (pageJurisdictions > totalNumberOfJurisdictionsTerritories / 20) {
      setHasMoreJurisdictions(false);
      return jurisdictionsTerritories;
    }
    setPageJurisdictions(pageJurisdictions + 1);
    return jurisdictionsTerritories;
  };

  // Use effect hooks
  useEffect(() => {
    setHasMore(true);
    getTerritoriesPage(1);
  }, [inputLocationText]);

  useEffect(() => {
    if (pageJurisdictions !== 1) getJurisdictionTerritoriesPage();
  }, [pageJurisdictions]);

  useEffect(() => {
    getJurisdictionTerritoriesPage(1);
  }, [inputJurisdictionsText]);

  // Handle signup success/failure
  useEffect(() => {
    if (isUserSignupSucceed) {
      setSuccess(true);
      setIsLoading(false);
    }
  }, [isUserSignupSucceed]);

  useEffect(() => {
    if (isUserSignupFailed) {
      setIsLoading(false);
      setErrors((prev) => ({
        ...prev,
        email:
          "Une erreur s'est produite lors de l'inscription. L'email existe peut-être déjà.",
      }));
    }
  }, [isUserSignupFailed]);

  useEffect(() => {
    return () => {
      removeSignUpFailure();
    };
  }, [removeSignUpFailure]);

  // Specializations configuration (same as EditExpertPage)
  const specializationKeys = [
    "CORPORATE_LAW",
    "CRIMINAL_LAW",
    "FAMILY_LAW",
    "INTELLECTUAL_PROPERTY",
    "TAX_LAW",
    "ENVIRONMENTAL_LAW",
    "LABOR_LAW",
    "REAL_ESTATE_LAW",
    "IMMIGRATION_LAW",
    "CONSTITUTIONAL_LAW",
  ];

  const getSpecializationLabel = (specialization: string) => {
    const translationKey = `EDIT_EXPERT_PAGE_SPECIALIZATION_${specialization}`;
    const translated = t(translationKey);
    return translated === translationKey ? specialization : translated;
  };

  const expertiseDomainKeys = [
    "CERTIFIED_PUBLIC_ACCOUNTANT",
    "CHARTERED_ACCOUNTANT",
    "LAWYER",
    "NOTARY",
  ];

  // Helper functions
  const clearError = (field: keyof FormErrors) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      if (!inputFirstName) newErrors.firstName = "Le prénom est requis";
      if (!inputLastName) newErrors.lastName = "Le nom est requis";
      if (!inputEmail) {
        newErrors.email = "L'email est requis";
      } else if (!/\S+@\S+\.\S+/.test(inputEmail)) {
        newErrors.email = "Format d'email invalide";
      }
      if (!inputPhoneNumber) newErrors.phoneNumber = "Le téléphone est requis";
      if (!inputProfessionalPosition)
        newErrors.professionalPosition =
          "La position professionnelle est requise";
      if (!inputLocation) newErrors.location = "La localisation est requise";
    }

    if (step === 2) {
      if (!inputPassword) {
        newErrors.password = "Le mot de passe est requis";
      } else if (inputPassword.length < 8) {
        newErrors.password =
          "Le mot de passe doit contenir au moins 8 caractères";
      }
      if (inputPassword !== inputConfirmPassword) {
        newErrors.confirmPassword = "Les mots de passe ne correspondent pas";
      }
    }

    if (step === 3 && inputIsExpert) {
      if (!inputDescription)
        newErrors.description = "La description est requise";
      if (!inputSpecialties.length)
        newErrors.specialties = "Les spécialités sont requises";
      if (!inputJurisdictions.length)
        newErrors.jurisdictions = "Au moins une juridiction est requise";
    }

    if (step === 4) {
      if (!inputAcceptTerms)
        newErrors.acceptTerms =
          "Vous devez accepter les conditions d'utilisation";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep(4)) return;

    setIsLoading(true);

    let roles = inputIsExpert ? ["EXPERT"] : ["USER"];

    if (roles.includes("USER")) {
      roles.push("MANAGER");
    }

    signUp({
      entityId: uuid(),
      email: inputEmail,
      password: inputPassword,
      entityName: inputEntityName,
      firstName: inputFirstName,
      lastName: inputLastName,
      roles: roles,
      title: inputTitle,
      phoneNumber: inputPhoneNumber,
      professionalPosition: inputProfessionalPosition,
      location: inputLocation?.id,
      language: inputLanguage,
      isExpert: inputIsExpert,
      expertiseDomain: inputExpertiseDomain,
      specialties: inputSpecialties,
      description: inputDescription,
      languages: inputLanguages,
      barAssociation: inputBarAssociation,
      educations: inputEducations,
      experiences: inputExperiences,
      jurisdictions: inputJurisdictions.map((t) => t.id),
    });

    setIsLoading(false);
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const addEducation = () => {
    const newEducation: Education = {
      id: uuid(),
      degreeName: "",
      institution: "",
      endYear: new Date().getFullYear(),
    };
    setInputEducations([...inputEducations, newEducation]);
  };

  const removeEducation = (id: string) => {
    setInputEducations(inputEducations.filter((edu) => edu.id !== id));
  };

  const updateEducation = (
    id: string,
    field: keyof Education,
    value: string | number
  ) => {
    setInputEducations(
      inputEducations.map((edu) =>
        edu.id === id ? { ...edu, [field]: value } : edu
      )
    );
  };

  const addExperience = () => {
    const newExperience: Experience = {
      id: uuid(),
      jobTitle: "",
      companyName: "",
      startDate: null,
      endDate: null,
      current: false,
    };
    setInputExperiences([...inputExperiences, newExperience]);
  };

  const removeExperience = (id: string) => {
    setInputExperiences(inputExperiences.filter((exp) => exp.id !== id));
  };

  const updateExperience = (
    id: string,
    field: keyof Experience,
    value: string | boolean | Dayjs | null
  ) => {
    setInputExperiences(
      inputExperiences.map((exp) =>
        exp.id === id ? { ...exp, [field]: value } : exp
      )
    );
  };

  return (
    <ThemeProvider theme={CustomTheme}>
      <div className="min-h-screen bg-white">
        <Header />
        <main>
          {success && (
            <section className="py-20 lg:py-32 bg-gradient-to-b from-gray-50 to-white">
              <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                <Card className="border-2 border-green-200 bg-green-50 rounded-2xl shadow-xl">
                  <CardContent className="p-12 text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
                      <CheckCircle className="h-10 w-10 text-green-600" />
                    </div>
                    <h1 className="text-3xl font-bold text-green-900 mb-4">
                      Inscription réussie !
                    </h1>
                    <p className="text-green-800 mb-8 text-lg">
                      Votre compte a été créé avec succès. Un email de
                      vérification a été envoyé à <strong>{inputEmail}</strong>.
                    </p>
                    <div className="space-y-4">
                      <Button
                        onClick={() => (window.location.href = "/signin")}
                        className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl"
                      >
                        Se connecter maintenant
                      </Button>
                      <p className="text-sm text-green-700">
                        Vous pouvez maintenant vous connecter et commencer à
                        utiliser Docuveritas.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>
          )}
          {!success && (
            <section className="py-20 bg-gradient-to-b from-gray-50 to-white min-h-screen">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-12">
                  <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                    Rejoignez <span className="text-blue-600">Docuveritas</span>
                  </h1>
                  <p className="text-xl text-gray-600 mb-8">
                    Créez votre compte et accédez à la plus complète plateforme
                    juridique intelligente
                  </p>

                  {/* Progress Steps */}
                  <div className="flex justify-center mb-8">
                    <div className="flex items-center space-x-4">
                      {[1, 2, 3, 4].map((step) => (
                        <div key={step} className="flex items-center">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                              step <= currentStep
                                ? "bg-blue-500 text-white"
                                : "bg-blue-100 text-blue-500"
                            }`}
                          >
                            {step}
                          </div>
                          {step < 4 && (
                            <div
                              className={`w-12 h-0.5 ${
                                step < currentStep
                                  ? "bg-blue-500"
                                  : "bg-blue-100"
                              }`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <Card className="border-2 border-gray-200 rounded-2xl shadow-xl">
                  <CardHeader className="text-center pb-8">
                    <CardTitle className="text-2xl font-bold text-gray-900">
                      {currentStep === 1 && "Informations personnelles"}
                      {currentStep === 2 && "Sécurité du compte"}
                      {currentStep === 3 && "Type de compte"}
                      {currentStep === 4 && "Finalisation"}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-8 pb-8">
                    <form onSubmit={handleSubmit}>
                      {/* Step 1: Personal Information */}
                      {currentStep === 1 && (
                        <div className="space-y-6">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                              <SelectLabeled
                                label="Titre"
                                value={inputTitle}
                                onChange={(e) => {
                                  setInputTitle(e.target.value);
                                  clearError("title");
                                }}
                                options={[
                                  { value: "Mr.", label: "Mr." },
                                  { value: "Mme", label: "Mme" },
                                ]}
                              />
                              {errors.title && (
                                <p className="text-red-600 text-sm mt-1">
                                  {errors.title}
                                </p>
                              )}
                            </div>
                            <div>
                              <TextFieldLabeled
                                fullWidth
                                label="Prénom *"
                                name="firstName"
                                autoComplete="given-name"
                                value={inputFirstName}
                                onChange={(e) => {
                                  setInputFirstName(e.target.value);
                                  clearError("firstName");
                                }}
                                placeholder="Votre prénom"
                              />
                              {errors.firstName && (
                                <p className="text-red-600 text-sm mt-1">
                                  {errors.firstName}
                                </p>
                              )}
                            </div>
                            <div>
                              <TextFieldLabeled
                                fullWidth
                                label="Nom *"
                                name="lastName"
                                autoComplete="family-name"
                                value={inputLastName}
                                onChange={(e) => {
                                  setInputLastName(e.target.value);
                                  clearError("lastName");
                                }}
                                placeholder="Votre nom"
                              />
                              {errors.lastName && (
                                <p className="text-red-600 text-sm mt-1">
                                  {errors.lastName}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <TextFieldLabeled
                                fullWidth
                                type="email"
                                label="Email professionnel *"
                                name="email"
                                autoComplete="email"
                                value={inputEmail}
                                onChange={(e) => {
                                  setInputEmail(e.target.value);
                                  clearError("email");
                                }}
                                placeholder="votre.email@entreprise.com"
                              />
                              {errors.email && (
                                <p className="text-red-600 text-sm mt-1">
                                  {errors.email}
                                </p>
                              )}
                            </div>
                            <div>
                              <TextFieldLabeled
                                fullWidth
                                type="tel"
                                label="Téléphone *"
                                name="phoneNumber"
                                autoComplete="tel"
                                value={inputPhoneNumber}
                                onChange={(e) => {
                                  setInputPhoneNumber(e.target.value);
                                  clearError("phoneNumber");
                                }}
                                placeholder="01 23 45 67 89"
                              />
                              {errors.phoneNumber && (
                                <p className="text-red-600 text-sm mt-1">
                                  {errors.phoneNumber}
                                </p>
                              )}
                            </div>
                          </div>

                          <div>
                            <TextFieldLabeled
                              fullWidth
                              label="Nom de l'entité/organisation"
                              value={inputEntityName}
                              onChange={(e) => {
                                setInputEntityName(e.target.value);
                                clearError("entityName");
                              }}
                              placeholder="Cabinet d'avocats, Entreprise, Institution..."
                            />
                            {errors.entityName && (
                              <p className="text-red-600 text-sm mt-1">
                                {errors.entityName}
                              </p>
                            )}
                          </div>

                          <div>
                            <TextFieldLabeled
                              fullWidth
                              label="Profession *"
                              value={inputProfessionalPosition}
                              onChange={(e) => {
                                setInputProfessionalPosition(e.target.value);
                                clearError("professionalPosition");
                              }}
                              placeholder="Avocat d'affaires, Directeur financier, Juriste..."
                            />
                            {errors.professionalPosition && (
                              <p className="text-red-600 text-sm mt-1">
                                {errors.professionalPosition}
                              </p>
                            )}
                          </div>

                          <div>
                            <AutocompleteInfiniteScroll<Territory>
                              loadOptions={fetchMoreData}
                              clearOnBlur={false}
                              initialOptions={territories}
                              hasMore={hasMore}
                              isLoading={isLoadingTerritories}
                              isOptionEqualToValue={(t, v) => t.id === v.id}
                              id="location-autocomplete"
                              label="Ville *"
                              value={inputLocation}
                              onChange={(_, v) => {
                                setInputLocation(v);
                                clearError("location");
                              }}
                              inputValue={inputLocationText}
                              onInputChange={(e, v) => setInputLocationText(v)}
                              getOptionLabel={(t) =>
                                `${t.name}, ${t.parents[0]?.parents[0]?.name}`
                              }
                              sx={{
                                "& .MuiInputBase-root": {
                                  border: "2px solid #e5e7eb",
                                  borderRadius: "12px",
                                  "&:focus-within": {
                                    borderColor: "#64748b",
                                  },
                                },
                              }}
                            />
                          </div>
                          {errors.location && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.location}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Step 2: Security */}
                      {currentStep === 2 && (
                        <div className="space-y-6">
                          <div>
                            <Label
                              htmlFor="password"
                              className="text-sm font-medium text-gray-700"
                            >
                              Mot de passe *
                            </Label>
                            <div className="relative mt-1">
                              <Input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                value={inputPassword}
                                onChange={(e) => {
                                  setInputPassword(e.target.value);
                                  clearError("password");
                                }}
                                className="border-2 border-gray-200 focus:border-slate-500 rounded-xl pr-12"
                                placeholder="Minimum 8 caractères"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                {showPassword ? (
                                  <EyeOff className="h-5 w-5" />
                                ) : (
                                  <Eye className="h-5 w-5" />
                                )}
                              </button>
                            </div>
                            {errors.password && (
                              <p className="text-red-600 text-sm mt-1">
                                {errors.password}
                              </p>
                            )}
                          </div>

                          <div>
                            <Label
                              htmlFor="confirmPassword"
                              className="text-sm font-medium text-gray-700"
                            >
                              Confirmer le mot de passe *
                            </Label>
                            <div className="relative mt-1">
                              <Input
                                id="confirmPassword"
                                type={showConfirmPassword ? "text" : "password"}
                                value={inputConfirmPassword}
                                onChange={(e) => {
                                  setInputConfirmPassword(e.target.value);
                                  clearError("confirmPassword");
                                }}
                                className="border-2 border-gray-200 focus:border-slate-500 rounded-xl pr-12"
                                placeholder="Confirmer votre mot de passe"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                {showConfirmPassword ? (
                                  <EyeOff className="h-5 w-5" />
                                ) : (
                                  <Eye className="h-5 w-5" />
                                )}
                              </button>
                            </div>
                            {errors.confirmPassword && (
                              <p className="text-red-600 text-sm mt-1">
                                {errors.confirmPassword}
                              </p>
                            )}
                          </div>

                          <div>
                            <SelectLabeled
                              label="Langue préférée"
                              value={inputLanguage}
                              onChange={(e) => setInputLanguage(e.target.value)}
                              options={[
                                { value: "fr", label: "Français" },
                                { value: "en", label: "English" },
                                { value: "ar", label: "العربية" },
                              ]}
                            />
                          </div>

                          <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                            <h3 className="font-semibold text-gray-900 mb-3">
                              Critères de sécurité du mot de passe :
                            </h3>
                            <ul className="space-y-2 text-sm text-gray-600">
                              <li
                                className={`flex items-center ${
                                  inputPassword.length >= 8
                                    ? "text-green-600"
                                    : ""
                                }`}
                              >
                                <CheckCircle
                                  className={`h-4 w-4 mr-2 ${
                                    inputPassword.length >= 8
                                      ? "text-green-600"
                                      : "text-gray-400"
                                  }`}
                                />
                                Au moins 8 caractères
                              </li>
                              <li
                                className={`flex items-center ${
                                  /[A-Z]/.test(inputPassword)
                                    ? "text-green-600"
                                    : ""
                                }`}
                              >
                                <CheckCircle
                                  className={`h-4 w-4 mr-2 ${
                                    /[A-Z]/.test(inputPassword)
                                      ? "text-green-600"
                                      : "text-gray-400"
                                  }`}
                                />
                                Une lettre majuscule
                              </li>
                              <li
                                className={`flex items-center ${
                                  /[0-9]/.test(inputPassword)
                                    ? "text-green-600"
                                    : ""
                                }`}
                              >
                                <CheckCircle
                                  className={`h-4 w-4 mr-2 ${
                                    /[0-9]/.test(inputPassword)
                                      ? "text-green-600"
                                      : "text-gray-400"
                                  }`}
                                />
                                Un chiffre
                              </li>
                            </ul>
                          </div>
                        </div>
                      )}

                      {/* Step 3: Account Type */}
                      {currentStep === 3 && (
                        <div className="space-y-6">
                          <div className="text-center mb-8">
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">
                              Choisissez votre type de compte
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <Card
                                className={`cursor-pointer border-2 rounded-xl transition-all ${
                                  !inputIsExpert
                                    ? "border-slate-500 bg-slate-50"
                                    : "border-gray-200 hover:border-gray-300"
                                }`}
                                onClick={() => setInputIsExpert(false)}
                              >
                                <CardContent className="p-6 text-center">
                                  <User className="h-12 w-12 mx-auto mb-4 text-slate-500" />
                                  <h4 className="font-semibold text-gray-900 mb-2">
                                    Utilisateur
                                  </h4>
                                  <p className="text-sm text-gray-600">
                                    Accès aux recherches, chatbot IA,
                                    certifications de réponses IA et
                                    consultation de documents
                                  </p>
                                </CardContent>
                              </Card>

                              <Card
                                className={`cursor-pointer border-2 rounded-xl transition-all ${
                                  inputIsExpert
                                    ? "border-slate-500 bg-slate-50"
                                    : "border-gray-200 hover:border-gray-300"
                                }`}
                                onClick={() => setInputIsExpert(true)}
                              >
                                <CardContent className="p-6 text-center">
                                  <UserCheck className="h-12 w-12 mx-auto mb-4 text-slate-500" />
                                  <h4 className="font-semibold text-gray-900 mb-2">
                                    Expert Juridique
                                  </h4>
                                  <p className="text-sm text-gray-600">
                                    Consultation de documents + Réponse aux
                                    demandes de certification de réponses IA
                                  </p>
                                </CardContent>
                              </Card>
                            </div>
                          </div>

                          {inputIsExpert && (
                            <div className="space-y-6 border-t pt-8">
                              <h4 className="text-lg font-semibold text-gray-900">
                                Informations d'expert
                              </h4>

                              <Alert>
                                <AlertDescription>
                                  {t("SIGN_UP_EXPERT_VERIFICATION_NOTICE")}
                                </AlertDescription>
                              </Alert>

                              <div>
                                <SelectLabeled
                                  label={t(
                                    "EDIT_EXPERT_PAGE_LABEL_EXPERTISE_DOMAIN"
                                  )}
                                  value={inputExpertiseDomain}
                                  onChange={(e) =>
                                    setInputExpertiseDomain(e.target.value)
                                  }
                                  options={[
                                    {
                                      value: "",
                                      label: t(
                                        "EDIT_EXPERT_PAGE_OPTION_SELECT_EXPERTISE_DOMAIN"
                                      ),
                                    },
                                    ...expertiseDomainKeys.map((option) => ({
                                      value: option,
                                      label: t(
                                        `EDIT_EXPERT_PAGE_EXPERTISE_DOMAIN_${option}`
                                      ),
                                    })),
                                  ]}
                                />
                              </div>

                              <div>
                                <AutocompleteLabeled
                                  label={t(
                                    "EDIT_EXPERT_PAGE_LABEL_SPECIALIZATIONS"
                                  )}
                                  multiple
                                  freeSolo
                                  options={specializationKeys}
                                  value={inputSpecialties}
                                  onChange={(_, value) => {
                                    setInputSpecialties(value);
                                    clearError("specialties");
                                  }}
                                  getOptionLabel={(option) =>
                                    getSpecializationLabel(option)
                                  }
                                  isOptionEqualToValue={(option, value) =>
                                    option === value
                                  }
                                  renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                      const tagProps = getTagProps({ index });
                                      const { key, ...other } = tagProps;
                                      return (
                                        <Chip
                                          key={key}
                                          {...other}
                                          label={getSpecializationLabel(option)}
                                        />
                                      );
                                    })
                                  }
                                />
                                {errors.specialties && (
                                  <p className="text-red-600 text-sm mt-1">
                                    {errors.specialties}
                                  </p>
                                )}
                              </div>

                              <div>
                                <AutocompleteLabeled
                                  label="Langues parlées"
                                  multiple
                                  disableCloseOnSelect
                                  options={languages}
                                  value={inputLanguages}
                                  onChange={(_, value) =>
                                    setInputLanguages(value)
                                  }
                                />
                              </div>

                              <div>
                                <AutocompleteInfiniteScroll<Territory>
                                  id="jurisdictions-autocomplete"
                                  label="Juridictions *"
                                  multiple
                                  isOptionEqualToValue={(t, v) => t.id === v.id}
                                  value={inputJurisdictions}
                                  onChange={(_, val) => {
                                    setInputJurisdictions(val as Territory[]);
                                    clearError("jurisdictions");
                                  }}
                                  inputValue={inputJurisdictionsText}
                                  onInputChange={(_, val) =>
                                    setInputJurisdictionsText(val)
                                  }
                                  initialOptions={jurisdictionsTerritories}
                                  loadOptions={fetchMoreJurisdictionsData}
                                  hasMore={hasMoreJurisdictions}
                                  isLoading={isLoadingJurisdictions}
                                  getOptionLabel={(t) =>
                                    `${t.name}, ${t.countryCode}`
                                  }
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      border: "2px solid #e5e7eb",
                                      borderRadius: "12px",
                                      "&:focus-within": {
                                        borderColor: "#64748b",
                                      },
                                    },
                                  }}
                                />
                                {errors.jurisdictions && (
                                  <p className="text-red-600 text-sm mt-1">
                                    {errors.jurisdictions}
                                  </p>
                                )}
                              </div>

                              <div>
                                <TextFieldLabeled
                                  fullWidth
                                  multiline
                                  minRows={4}
                                  label="Description professionnelle *"
                                  value={inputDescription}
                                  onChange={(e) => {
                                    setInputDescription(e.target.value);
                                    clearError("description");
                                  }}
                                  placeholder="Décrivez votre expérience et expertise..."
                                />
                                {errors.description && (
                                  <p className="text-red-600 text-sm mt-1">
                                    {errors.description}
                                  </p>
                                )}
                              </div>

                              <div>
                                <TextFieldLabeled
                                  fullWidth
                                  label="Ordre d'inscription"
                                  value={inputBarAssociation}
                                  onChange={(e) =>
                                    setInputBarAssociation(e.target.value)
                                  }
                                  placeholder="Barreau de Paris..."
                                />
                              </div>

                              {/* Educations */}
                              <div>
                                <div className="flex items-center justify-between mb-4">
                                  <Label className="text-sm font-medium text-gray-700">
                                    Formation
                                  </Label>
                                  <Button
                                    type="button"
                                    onClick={addEducation}
                                    variant="outline"
                                    size="sm"
                                    className="border-slate-500 text-slate-500 hover:bg-slate-50 bg-transparent"
                                  >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Ajouter
                                  </Button>
                                </div>
                                {inputEducations.map((education) => (
                                  <div
                                    key={education.id}
                                    className="border border-gray-200 rounded-xl p-4 mb-4"
                                  >
                                    <div className="flex justify-between items-start mb-4">
                                      <h5 className="font-medium text-gray-900">
                                        Formation
                                      </h5>
                                      <Button
                                        type="button"
                                        onClick={() =>
                                          removeEducation(education.id)
                                        }
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <TextFieldLabeled
                                          fullWidth
                                          label="Diplôme"
                                          value={education.degreeName}
                                          onChange={(e) =>
                                            updateEducation(
                                              education.id,
                                              "degreeName",
                                              e.target.value
                                            )
                                          }
                                          placeholder="Master en droit..."
                                        />
                                      </div>
                                      <div>
                                        <TextFieldLabeled
                                          fullWidth
                                          label="Institution"
                                          value={education.institution}
                                          onChange={(e) =>
                                            updateEducation(
                                              education.id,
                                              "institution",
                                              e.target.value
                                            )
                                          }
                                          placeholder="Université..."
                                        />
                                      </div>
                                      <div>
                                        <TextFieldLabeled
                                          fullWidth
                                          type="number"
                                          label="Année d'obtention"
                                          value={education.endYear}
                                          onChange={(e) =>
                                            updateEducation(
                                              education.id,
                                              "endYear",
                                              parseInt(e.target.value) ||
                                                new Date().getFullYear()
                                            )
                                          }
                                          placeholder="2022"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {/* Experiences */}
                              <div>
                                <div className="flex items-center justify-between mb-4">
                                  <Label className="text-sm font-medium text-gray-700">
                                    Expérience professionnelle
                                  </Label>
                                  <Button
                                    type="button"
                                    onClick={addExperience}
                                    variant="outline"
                                    size="sm"
                                    className="border-slate-500 text-slate-500 hover:bg-slate-50 bg-transparent"
                                  >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Ajouter
                                  </Button>
                                </div>
                                {inputExperiences.map((experience) => (
                                  <div
                                    key={experience.id}
                                    className="border border-gray-200 rounded-xl p-4 mb-4"
                                  >
                                    <div className="flex justify-between items-start mb-4">
                                      <h5 className="font-medium text-gray-900">
                                        Expérience
                                      </h5>
                                      <Button
                                        type="button"
                                        onClick={() =>
                                          removeExperience(experience.id)
                                        }
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <TextFieldLabeled
                                          fullWidth
                                          label="Poste"
                                          value={experience.jobTitle}
                                          onChange={(e) =>
                                            updateExperience(
                                              experience.id,
                                              "jobTitle",
                                              e.target.value
                                            )
                                          }
                                          placeholder="Avocat senior..."
                                        />
                                      </div>
                                      <div>
                                        <TextFieldLabeled
                                          fullWidth
                                          label="Entreprise"
                                          value={experience.companyName}
                                          onChange={(e) =>
                                            updateExperience(
                                              experience.id,
                                              "companyName",
                                              e.target.value
                                            )
                                          }
                                          placeholder="Cabinet XYZ..."
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs text-gray-600">
                                          Date de début
                                        </Label>
                                        <Input
                                          type="date"
                                          value={
                                            experience.startDate
                                              ? dayjs(
                                                  experience.startDate
                                                ).format("YYYY-MM-DD")
                                              : ""
                                          }
                                          onChange={(e) =>
                                            updateExperience(
                                              experience.id,
                                              "startDate",
                                              e.target.value
                                                ? dayjs(e.target.value)
                                                : null
                                            )
                                          }
                                          className="mt-1 border border-gray-200 focus:border-slate-500 rounded-lg"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs text-gray-600">
                                          Date de fin
                                        </Label>
                                        <Input
                                          type="date"
                                          value={
                                            experience.endDate
                                              ? dayjs(
                                                  experience.endDate
                                                ).format("YYYY-MM-DD")
                                              : ""
                                          }
                                          onChange={(e) =>
                                            updateExperience(
                                              experience.id,
                                              "endDate",
                                              e.target.value
                                                ? dayjs(e.target.value)
                                                : null
                                            )
                                          }
                                          className="mt-1 border border-gray-200 focus:border-slate-500 rounded-lg"
                                          disabled={experience.current}
                                        />
                                      </div>
                                    </div>
                                    <div className="mt-4">
                                      <div className="flex items-center space-x-2">
                                        <Checkbox
                                          id={`current-${experience.id}`}
                                          checked={experience.current}
                                          onCheckedChange={(checked) =>
                                            updateExperience(
                                              experience.id,
                                              "current",
                                              checked as boolean
                                            )
                                          }
                                        />
                                        <Label
                                          htmlFor={`current-${experience.id}`}
                                          className="text-sm text-gray-600"
                                        >
                                          Poste actuel
                                        </Label>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Step 4: Terms and Conditions */}
                      {currentStep === 4 && (
                        <div className="space-y-6">
                          <div className="text-center mb-8">
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">
                              Récapitulatif de votre inscription
                            </h3>
                          </div>

                          <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="font-medium text-gray-700">
                                  Nom complet :
                                </span>
                                <span className="ml-2 text-gray-900">
                                  {[inputTitle, inputFirstName, inputLastName]
                                    .filter(Boolean)
                                    .join(" ")}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">
                                  Email :
                                </span>
                                <span className="ml-2 text-gray-900">
                                  {inputEmail}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">
                                  Téléphone :
                                </span>
                                <span className="ml-2 text-gray-900">
                                  {inputPhoneNumber}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">
                                  Type de compte :
                                </span>
                                <span className="ml-2 text-gray-900">
                                  {inputIsExpert
                                    ? "Expert Juridique"
                                    : "Utilisateur"}
                                </span>
                              </div>
                              {inputEntityName && (
                                <div>
                                  <span className="font-medium text-gray-700">
                                    Entité :
                                  </span>
                                  <span className="ml-2 text-gray-900">
                                    {inputEntityName}
                                  </span>
                                </div>
                              )}
                              <div>
                                <span className="font-medium text-gray-700">
                                  Position :
                                </span>
                                <span className="ml-2 text-gray-900">
                                  {inputProfessionalPosition}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">
                                  Localisation :
                                </span>
                                <span className="ml-2 text-gray-900">
                                  {inputLocation?.name
                                    ? `${inputLocation.name}, ${inputLocation.parents[0]?.parents[0]?.name}`
                                    : "Non spécifiée"}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div className="flex items-start space-x-3">
                              <Checkbox
                                id="acceptTerms"
                                checked={inputAcceptTerms}
                                onCheckedChange={(checked) => {
                                  setInputAcceptTerms(checked as boolean);
                                  clearError("acceptTerms");
                                }}
                              />
                              <Label
                                htmlFor="acceptTerms"
                                className="text-sm text-gray-700 leading-relaxed"
                              >
                                J'accepte les{" "}
                                <a
                                  href="/cgu"
                                  className="text-slate-500 hover:underline"
                                >
                                  Conditions Générales d'Utilisation
                                </a>{" "}
                                et la{" "}
                                <a
                                  href="/confidentialite"
                                  className="text-slate-500 hover:underline"
                                >
                                  Politique de Confidentialité
                                </a>{" "}
                                de Docuveritas *
                              </Label>
                            </div>
                            {errors.acceptTerms && (
                              <p className="text-red-600 text-sm">
                                {errors.acceptTerms}
                              </p>
                            )}

                            <div className="flex items-start space-x-3">
                              <Checkbox id="newsletter" />
                              <Label
                                htmlFor="newsletter"
                                className="text-sm text-gray-700"
                              >
                                Je souhaite recevoir les actualités et offres
                                spéciales de Docuveritas (optionnel)
                              </Label>
                            </div>
                          </div>

                          <Alert className="border-blue-200 bg-blue-50">
                            <AlertDescription className="text-blue-800">
                              <strong>Prochaines étapes :</strong> Après votre
                              inscription, vous recevrez un email de
                              vérification. Une fois votre compte vérifié, vous
                              pourrez commencer à utiliser Docuveritas
                              immédiatement.
                            </AlertDescription>
                          </Alert>
                        </div>
                      )}

                      {/* Navigation Buttons */}
                      <div className="flex justify-between pt-8 border-t border-gray-200">
                        {currentStep > 1 && (
                          <Button
                            type="button"
                            onClick={prevStep}
                            variant="outline"
                            className="border-gray-300 text-gray-700 hover:bg-gray-50 bg-transparent"
                          >
                            Précédent
                          </Button>
                        )}

                        {currentStep < 4 ? (
                          <Button
                            type="button"
                            onClick={nextStep}
                            className="bg-slate-500 hover:bg-slate-600 text-white ml-auto"
                          >
                            Suivant
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            type="submit"
                            disabled={isLoading}
                            className="bg-slate-500 hover:bg-slate-600 text-white ml-auto"
                          >
                            {isLoading ? (
                              <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>Création du compte...</span>
                              </div>
                            ) : (
                              <>
                                Créer mon compte
                                <CheckCircle className="ml-2 h-4 w-4" />
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </form>
                  </CardContent>
                </Card>

                {/* Login Link */}
                <div className="text-center mt-8">
                  <p className="text-gray-600">
                    Vous avez déjà un compte ?{" "}
                    <a
                      href="/signin"
                      className="text-blue-500 font-semibold hover:text-blue-600 hover:underline"
                    >
                      Se connecter
                    </a>
                  </p>
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </ThemeProvider>
  );
};

export const RegisterPage = connect(
  (state: RootState) => ({
    isUserSignupSucceed: isUserSignupSucceed(state),
    isUserSignupFailed: isUserSignupFailed(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        signUp: signUpAction,
        removeSignUpFailure: removeSignUpActionFailure,
      },
      dispatch
    )
)(_RegisterPage);

export default RegisterPage;
